import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import { Tooltip, Typography } from '@zendesk/forethought-elements';
import { formatTimestamp } from '../helpers';
import { AiInstructionsTrigger } from './AiInstructionsTrigger';
import { NonEmptyArray } from './buildRenderBlocks';
import { AiInstructionsTranscriptComponent } from './types';

export const CollapsibleKnowledgeCoachingGroup = ({
  transcriptComponents,
}: {
  transcriptComponents: NonEmptyArray<AiInstructionsTranscriptComponent>;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const instructionIds = useMemo(
    () => transcriptComponents.flatMap(c => c.ai_instruction_ids),
    [transcriptComponents],
  );

  const tooltipContent = useMemo(
    () => formatTimestamp(transcriptComponents[0].timestamp + 'Z'),
    [transcriptComponents],
  );

  const toggle = () => setIsExpanded(prev => !prev);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <Box display='flex' flexDirection='column' gap='6px' width='100%'>
      <Tooltip
        placement='top'
        sx={{ maxWidth: '100%', width: '100%' }}
        tooltipContent={tooltipContent}
      >
        <Box
          aria-expanded={isExpanded}
          onClick={toggle}
          onKeyDown={handleKeyDown}
          role='button'
          sx={{
            alignItems: 'center',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.colors.slate[100]}`,
            borderRadius: '5px',
            cursor: 'pointer',
            display: 'flex',
            gap: '6px',
            justifyContent: 'space-between',
            padding: '6px 12px',
            width: '100%',
          }}
          tabIndex={0}
        >
          <Typography
            color={theme.palette.colors.grey[600]}
            variant='font12Medium'
          >
            {t('collapsibleKnowledgeCoachingGroup.title', {
              count: instructionIds.length,
            })}
          </Typography>
          {isExpanded ? (
            <IconChevronDown size={16} />
          ) : (
            <IconChevronRight size={16} />
          )}
        </Box>
      </Tooltip>

      {isExpanded &&
        instructionIds.map((id, idx) => (
          <Tooltip
            key={`${idx}-${id}`}
            placement='top'
            sx={{ maxWidth: '100%', width: '100%' }}
            tooltipContent={tooltipContent}
          >
            <AiInstructionsTrigger aiInstructionId={id} />
          </Tooltip>
        ))}
    </Box>
  );
};
